import { useState } from "react";
import { API } from "@/lib/shared";

/**
 * InsightsRequestForm — formulario compacto exclusivo de /insights.
 * Envía al mismo endpoint /api/contact que el formulario general, pero
 * marca el mensaje con "[Insights request]" para que ventas priorice.
 */
const InsightsRequestForm = () => {
  const [status, setStatus] = useState({ state: "idle", error: null });
  const [form, setForm] = useState({
    nombre: "",
    empresa: "",
    correo: "",
  });

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.empresa.trim() || !form.correo.trim()) {
      setStatus({ state: "error", error: "Todos los campos son obligatorios." });
      return;
    }
    setStatus({ state: "loading", error: null });
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nombre: form.nombre.trim(),
          empresa: form.empresa.trim(),
          correo: form.correo.trim(),
          telefono: "",
          mensaje: "[Insights request] Quiero ver una demo de Caruna Insights.",
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus({ state: "success", error: null });
      setForm({ nombre: "", empresa: "", correo: "" });
    } catch (err) {
      setStatus({
        state: "error",
        error: "No pudimos enviar tu solicitud. Intenta de nuevo en unos minutos.",
      });
    }
  };

  return (
    <section
      id="insights-form"
      data-testid="insights-form-section"
      style={{
        background: "#F5F7F6",
        padding: "80px 24px",
        width: "100%",
      }}
    >
      <div
        style={{
          maxWidth: "760px",
          margin: "0 auto",
          background: "#FFFFFF",
          borderRadius: "20px",
          padding: "48px 40px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "6px",
            background: "linear-gradient(90deg, #0C7E6C, #8BC054)",
          }}
        />
        <p
          data-reveal
          style={{
            fontSize: "12px",
            letterSpacing: "2px",
            color: "#8A9A94",
            textTransform: "uppercase",
            marginBottom: "12px",
          }}
        >
          VER EN ACCIÓN
        </p>
        <h2
          data-testid="insights-form-headline"
          data-reveal
          style={{
            fontSize: "32px",
            fontWeight: 900,
            color: "#1D1D1B",
            letterSpacing: "-0.3px",
            marginBottom: "12px",
            lineHeight: 1.15,
          }}
        >
          Solicita ver los Insights de tu operación
        </h2>
        <p
          data-reveal
          style={{
            fontSize: "16px",
            color: "#4A5A54",
            lineHeight: 1.6,
            marginBottom: "32px",
          }}
        >
          Agenda una sesión con nuestro equipo y te mostramos con datos reales
          cómo Caruna Insights captura, transcribe y analiza cada conversación
          de tu negocio.
        </p>

        <form onSubmit={onSubmit} data-testid="insights-form" noValidate>
          <div style={{ display: "grid", gap: "16px", marginBottom: "20px" }}>
            <label style={{ display: "block" }}>
              <span
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#1D1D1B",
                  marginBottom: "6px",
                }}
              >
                Nombre completo
              </span>
              <input
                type="text"
                name="nombre"
                value={form.nombre}
                onChange={onChange}
                required
                data-testid="insights-form-nombre"
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: "15px",
                  border: "1px solid #D6DDDA",
                  borderRadius: "10px",
                  outline: "none",
                  background: "#FBFCFC",
                  color: "#1D1D1B",
                }}
              />
            </label>
            <label style={{ display: "block" }}>
              <span
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#1D1D1B",
                  marginBottom: "6px",
                }}
              >
                Empresa
              </span>
              <input
                type="text"
                name="empresa"
                value={form.empresa}
                onChange={onChange}
                required
                data-testid="insights-form-empresa"
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: "15px",
                  border: "1px solid #D6DDDA",
                  borderRadius: "10px",
                  outline: "none",
                  background: "#FBFCFC",
                  color: "#1D1D1B",
                }}
              />
            </label>
            <label style={{ display: "block" }}>
              <span
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#1D1D1B",
                  marginBottom: "6px",
                }}
              >
                Correo corporativo
              </span>
              <input
                type="email"
                name="correo"
                value={form.correo}
                onChange={onChange}
                required
                data-testid="insights-form-correo"
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: "15px",
                  border: "1px solid #D6DDDA",
                  borderRadius: "10px",
                  outline: "none",
                  background: "#FBFCFC",
                  color: "#1D1D1B",
                }}
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={status.state === "loading"}
            data-testid="insights-form-submit"
            style={{
              display: "inline-block",
              background: "#1D1D1B",
              color: "#FFFFFF",
              borderRadius: "999px",
              padding: "14px 36px",
              fontSize: "15px",
              fontWeight: 600,
              border: "none",
              cursor: status.state === "loading" ? "wait" : "pointer",
              opacity: status.state === "loading" ? 0.7 : 1,
            }}
          >
            {status.state === "loading"
              ? "Enviando..."
              : "Ver Insights en acción →"}
          </button>

          {status.state === "success" && (
            <p
              data-testid="insights-form-success"
              style={{
                marginTop: "18px",
                fontSize: "14px",
                color: "#0C7E6C",
                fontWeight: 600,
              }}
            >
              ¡Gracias! Nuestro equipo te contactará muy pronto para agendar la
              sesión.
            </p>
          )}
          {status.state === "error" && (
            <p
              data-testid="insights-form-error"
              style={{
                marginTop: "18px",
                fontSize: "14px",
                color: "#B23B3B",
                fontWeight: 600,
              }}
            >
              {status.error}
            </p>
          )}
        </form>
      </div>
    </section>
  );
};

export default InsightsRequestForm;
